// Client for the bulk-add analysis job API. The contract (start a job → poll it
// to completion) is served by the real Hono backend (packages/backend) in
// production via nginx, and by the MSW mock (mocks/handlers/bulk.ts) in dev/tests
// — same paths, same shapes. Responses are validated with the shared Zod schemas.
//
// The analyze call forwards the user's Tahiti Bearer so the backend can read
// existing words (batch-vs-DB conflict detection) as the logged-in user. The
// header is harmless against the MSW mock, which ignores it.
import { getToken } from '@/lib/tahiti-client';
import {
  type BulkJob,
  type BulkSession,
  analyzeResponseSchema,
  bulkJobSchema,
  bulkSessionSchema,
  draftListSchema,
} from '@/schemas/bulk';
import type { NoyauCode } from '@/schemas/word';

const ANALYZE_PATH = '/api/bulk/analyze';
const JOB_PATH = '/api/bulk/jobs';
const DRAFTS_PATH = '/api/bulk/drafts';

/** Authorization header carrying the current Tahiti session token, if any. */
function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Start an analysis job for a pasted blob under a chosen batch noyau (null =
 * indéterminé). Returns the job id to poll with {@link getBulkJob}.
 */
export async function analyzeBulk(text: string, noyau: NoyauCode | null): Promise<string> {
  const res = await fetch(ANALYZE_PATH, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ text, noyau }),
  });
  if (!res.ok) throw new Error(`Bulk analyze failed: ${res.status}`);
  return analyzeResponseSchema.parse(await res.json()).jobId;
}

/** Fetch the current state of an analysis job (progress, and result when done). */
export async function getBulkJob(jobId: string): Promise<BulkJob> {
  const res = await fetch(`${JOB_PATH}/${encodeURIComponent(jobId)}`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Bulk job poll failed: ${res.status}`);
  return bulkJobSchema.parse(await res.json());
}

// ── Draft persistence ─────────────────────────────────────────────────────────
// Server-side drafts so a review survives a device switch. The backend keys them
// by the Tahiti-resolved user (from the forwarded bearer); a 401 signals an
// expired session, surfaced so the provider can route to login. Responses are
// validated against the shared session schema (a trust boundary).

/** Raised when a draft request is rejected for lack of a valid session (HTTP 401). */
export class DraftUnauthorizedError extends Error {
  constructor() {
    super('Draft request unauthorized');
    this.name = 'DraftUnauthorizedError';
  }
}

/**
 * A permanent client error (HTTP 4xx other than 401) — e.g. a rejected payload.
 * Distinct from a network / 5xx failure: retrying won't help, so the autosave
 * loop stops instead of re-sending the same doomed request forever.
 */
export class DraftClientError extends Error {
  constructor(public readonly status: number) {
    super(`Draft request failed: ${status}`);
    this.name = 'DraftClientError';
  }
}

/** Fetch all of the current user's drafts (hydrate-on-mount). */
export async function listDrafts(): Promise<BulkSession[]> {
  const res = await fetch(DRAFTS_PATH, { credentials: 'include', headers: authHeaders() });
  if (res.status === 401) throw new DraftUnauthorizedError();
  if (!res.ok) throw new Error(`Draft list failed: ${res.status}`);
  return draftListSchema.parse(await res.json()).drafts;
}

/** Upsert one draft; returns the stored session (with the server-stamped updatedAt). */
export async function putDraft(session: BulkSession): Promise<BulkSession> {
  const res = await fetch(`${DRAFTS_PATH}/${encodeURIComponent(session.id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify(session),
  });
  if (res.status === 401) throw new DraftUnauthorizedError();
  if (res.status >= 400 && res.status < 500) throw new DraftClientError(res.status);
  if (!res.ok) throw new Error(`Draft save failed: ${res.status}`);
  return bulkSessionSchema.parse(await res.json());
}

/** Delete one draft (idempotent server-side). */
export async function deleteDraft(id: string): Promise<void> {
  const res = await fetch(`${DRAFTS_PATH}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders(),
  });
  if (res.status === 401) throw new DraftUnauthorizedError();
  if (!res.ok) throw new Error(`Draft delete failed: ${res.status}`);
}
